import { createClient } from '@/utils/supabase/client'

// Reading the access token WITHOUT going through `supabase.auth.getSession()`.
//
// getSession() can hang offline: with `autoRefreshToken` on (native client), an
// expired/near-expiry token makes it kick off a refresh network call that never
// completes without a connection. Awaiting it with no timeout stalls whatever
// is above it — that froze the camera shutter (see create-analysis.ts) and is a
// latent hang on every native `/api` call (api.ts) and any page that resolves
// the internal user id (useCurrentInternalUserId). These helpers sidestep it.

export function readPersistedAccessToken(): string | null {
  try {
    if (typeof window === 'undefined') return null
    // Read ONLY this build's project key (sb-<ref>-auth-token, supabase-js's
    // default storageKey). Staging and prod builds share the ai.zerosnag.app
    // bundle id and therefore one WebView localStorage — a device that ran
    // both has BOTH projects' session keys side by side. Scanning for any
    // sb-*-auth-token can pick the OTHER project's stale token: every native
    // /api call then 401s, the intent worker pauses ("Bitte erneut anmelden"),
    // and re-login can't clear it because it only rewrites this project's key.
    const supabaseUrl = process.env.VITE_SUPABASE_URL
    if (!supabaseUrl) return null
    const projectRef = new URL(supabaseUrl).hostname.split('.')[0]
    const raw = window.localStorage.getItem(`sb-${projectRef}-auth-token`)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const at = parsed?.access_token ?? parsed?.currentSession?.access_token
    return typeof at === 'string' ? at : null
  } catch {
    return null
  }
}

// Get an access token without ever blocking on a network refresh. Prefer the
// persisted token (no network, never hangs); fall back to getSession() only
// when storage is empty, and even then race a short timeout so an offline
// refresh-hang can't wedge the caller. Returns null if no token is resolvable.
export async function getAccessTokenNoHang(timeoutMs = 2000): Promise<string | null> {
  const persisted = readPersistedAccessToken()
  if (persisted) return persisted
  return Promise.race([
    createClient().auth.getSession().then((r) => r.data.session?.access_token ?? null).catch(() => null),
    new Promise<null>((r) => setTimeout(() => r(null), timeoutMs)),
  ])
}
